import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function () { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import GeometryLayer from "../../src/Layer/GeometryLayer";
import { MAIN_LOOP_EVENTS } from "../../src/Core/MainLoop";
import ObjectRemovalHelper from "../../src/Process/ObjectRemovalHelper";
import TileObjectChart from "./charts/TileObjectChart";
import TileVisibilityChart from "./charts/TileVisibilityChart";
import GeometryDebug from "./GeometryDebug";
import Debug from "./Debug";
import OBBHelper from "./OBBHelper";

function applyToNodeFirstMaterial(view, root, layer, cb) {
  root.traverse(function (object) {
    if (object.material && object.layer === layer) {
      cb(object.material);
    }
  });
  view.notifyChange();
}

var selectedNode;
/**
 * Select tile
 *
 * @param      {View} view
 * @param      {Object} mouseOrEvt - mouse position in window coordinates (0, 0 = top-left)
 * or MouseEvent or TouchEvent.
 * @param      {boolean}  [showInfo=true] Show tile information in console.
 * @return     {TileMesh} Selected tile.
 */

function selectTileAt(view, mouseOrEvt) {
  var showInfo = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;

  if (selectedNode) {
    selectedNode.material.overlayAlpha = 0;
    selectedNode.material.showOutline = view.tileLayer.showOutline;
    view.notifyChange(selectedNode);
  }

  var picked = view.tileLayer.pickObjectsAt(view, mouseOrEvt);
  selectedNode = picked.length ? picked[0].object : undefined;

  if (selectedNode) {
    if (showInfo) {
      // eslint-disable-next-line no-console
      console.info(selectedNode);
    }

    selectedNode.material.overlayAlpha = 0.5;
    selectedNode.material.showOutline = true;
    view.notifyChange(selectedNode);
  }

  return selectedNode;
}

export default function createTileDebugUI(datDebugTool, view, layer, debugInstance) {
  var force = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;

  if (!view.isDebugMode && !force) {
    return;
  }

  debugInstance = debugInstance || new Debug(view, datDebugTool);
  layer = layer || view.tileLayer;
  var gui = GeometryDebug.createGeometryDebugUI(datDebugTool, view, layer);
  var objectChardId = "".concat(layer.id, "-nb-objects");
  var canvasObjectChardId = debugInstance.createChartContainer(objectChardId);
  var visibleChardId = "".concat(layer.id, "-nb-visible");
  var canvasVisibleChardId = debugInstance.createChartContainer(visibleChardId);
  debugInstance.charts.push(new TileObjectChart(canvasObjectChardId.getContext('2d'), layer));
  debugInstance.charts.push(new TileVisibilityChart(canvasVisibleChardId.getContext('2d'), layer));
  layer.showOutline = false;
  layer.wireframe = false;
  var state = {
    objectChart: true,
    visibilityChart: true,
    sseHelper: false
  }; // tiles outline

  gui.add(layer, 'showOutline').name('Show tiles').onChange(function (newValue) {
    layer.showOutline = newValue;
    applyToNodeFirstMaterial(view, layer.object3d, layer, function (material) {
      material.showOutline = newValue;
    });
  }); // tiles wireframe

  gui.add(layer, 'wireframe').name('Wireframe').onChange(function () {
    view.notifyChange(layer);
  }); // TileObjectChart visibility

  gui.add(state, 'objectChart').name('Object chart').onChange(function (newValue) {
    if (newValue) {
      document.getElementById(objectChardId).parentNode.style.display = 'block';
    } else {
      document.getElementById(objectChardId).parentNode.style.display = 'none';
    }

    debugInstance.updateChartDivSize();
    debugInstance.charts.forEach(function (c) {
      return c.update();
    });
  }); // TileVisibilityChart visibility

  gui.add(state, 'visibilityChart').name('Visibility chart').onChange(function (newValue) {
    if (newValue) {
      document.getElementById(visibleChardId).parentNode.style.display = 'block';
    } else {
      document.getElementById(visibleChardId).parentNode.style.display = 'none';
    }

    debugInstance.updateChartDivSize();
    debugInstance.charts.forEach(function (c) {
      return c.update();
    });
  }); // Bounding box control

  var obb_layer_id = "".concat(layer.id, "_obb_debug");
  var sb_layer_id = "".concat(layer.id, "_sb_debug");
  var geometrySphere = new THREE.SphereGeometry(1, 16, 16);

  function debugIdUpdate(context, layer, node) {
    if (!node.parent || !layer.visible) {
      ObjectRemovalHelper.removeChildrenAndCleanupRecursively(layer, node);
      return;
    } // filtering helper attached to node with the current debug layer


    var helper = node.link[layer.id];

    if (node.visible && node.material && node.material.visible) {
      if (!helper) {
        helper = new THREE.Group();
        helper.layer = layer;
        node.matrixWorld.decompose(helper.position, helper.quaternion, helper.scale);

        if (layer.id == obb_layer_id) {
          var obbHelper = new OBBHelper(node.obb);
          obbHelper.layer = layer;
          helper.add(obbHelper);
        } else if (layer.id == sb_layer_id) {
          var color = new THREE.Color(Math.random(), Math.random(), Math.random());
          var material = new THREE.MeshBasicMaterial({
            color: color.getHex(),
            wireframe: true
          });
          var sphereHelper = new THREE.Mesh(geometrySphere, material);
          sphereHelper.position.copy(node.boundingSphere.center);
          sphereHelper.scale.multiplyScalar(node.boundingSphere.radius);
          sphereHelper.scale.set(1, 1, 1).multiplyScalar(node.boundingSphere.radius);
          sphereHelper.layer = layer;
          helper.add(sphereHelper);
        }

        node.link[layer.id] = helper;
      }

      layer.object3d.add(helper);
      helper.updateMatrixWorld(true);
    } else if (helper) {
      layer.object3d.remove(helper);
    }
  }

  var DebugLayer = /*#__PURE__*/function (_GeometryLayer) {
    _inherits(DebugLayer, _GeometryLayer);

    var _super = _createSuper(DebugLayer);

    function DebugLayer(id) {
      var _this;

      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      _classCallCheck(this, DebugLayer);

      options.update = debugIdUpdate;
      _this = _super.call(this, id, options.object3d || new THREE.Group(), options);
      _this.isDebugLayer = true;
      return _this;
    }

    _createClass(DebugLayer, [{
      key: "preUpdate",
      value: function preUpdate(context, sources) {
        if (sources.has(this.parent)) {
          this.object3d.clear();
        }
      }
    }]);

    return DebugLayer;
  }(GeometryLayer);

  var obbLayer = new DebugLayer(obb_layer_id, {
    visible: false,
    cacheLifeTime: Infinity,
    source: false
  });
  view.addLayer(obbLayer).then(function (l) {
    gui.add(l, 'visible').name('Bounding boxes').onChange(function () {
      view.notifyChange(l);
    });
  });
  var sbLayer = new DebugLayer(sb_layer_id, {
    visible: false,
    cacheLifeTime: Infinity,
    source: false
  });
  view.addLayer(sbLayer).then(function (l) {
    gui.add(l, 'visible').name('Bounding Spheres').onChange(function () {
      view.notifyChange(l);
    });
  });
  var viewerDiv = document.getElementById('viewerDiv');
  var circle = document.createElement('span');
  circle.className = 'circleBase';
  viewerDiv.appendChild(circle);
  var centerNode = new THREE.Vector3();
  var actualNode;

  var animationFrameRequester = function animationFrameRequester() {
    TWEEN.update();
    view.notifyChange();
  };

  var removeAnimationRequester = function removeAnimationRequester() {
    TWEEN.removeAll();

    if (view._frameRequesters[MAIN_LOOP_EVENTS.BEFORE_RENDER].includes(animationFrameRequester)) {
      view.removeFrameRequester(MAIN_LOOP_EVENTS.BEFORE_RENDER, animationFrameRequester);
    }
  };

  function picking(event) {
    var selectNode = selectTileAt(view, event, false);

    if (selectNode) {
      circle.style.display = 'table-cell';
      centerNode.copy(selectNode.boundingSphere.center).applyMatrix4(selectNode.matrixWorld);
      var project = centerNode.project(view.camera.camera3D);
      var coords = view.normalizedToViewCoords(project);
      var size = selectNode.screenSize;

      if (actualNode != selectNode) {
        var actualSize = Number(circle.style.width.replace('px', ''));
        actualNode = selectNode;
        removeAnimationRequester();
        new TWEEN.Tween({
          size: actualSize
        }).to({
          size: size
        }, 500).easing(TWEEN.Easing.Sinusoidal.In).easing(TWEEN.Easing.Exponential.Out).onUpdate(function (object) {
          circle.style['line-height'] = "".concat(object.size, "px");
          circle.style.width = "".concat(object.size, "px");
          circle.style.height = "".concat(object.size, "px");
          circle.innerHTML = "".concat(Math.floor(object.size), " px");
          circle.style.left = "".concat(coords.x - object.size * 0.5, "px");
          circle.style.top = "".concat(coords.y - object.size * 0.5, "px");
        }).onComplete(removeAnimationRequester).start();
        view.addFrameRequester(MAIN_LOOP_EVENTS.BEFORE_RENDER, animationFrameRequester);
      }
    } else {
      circle.style.display = 'none';
    }
  }

  gui.add(state, 'sseHelper').name('Sse helper').onChange(function (v) {
    if (v) {
      window.addEventListener('mousemove', picking, false);
    } else {
      circle.style.display = 'none';
      removeAnimationRequester();
      window.removeEventListener('mousemove', picking);
    }
  });
  var currKey = null;
  window.addEventListener('mousedown', function (event) {
    if (currKey == 83) {
      selectTileAt(view, event);
    }
  });
  window.addEventListener('keydown', function (event) {
    currKey = event.which;
  });
  window.addEventListener('keyup', function () {
    currKey = null;
  });
}